import { gql, useApolloClient } from '@apollo/client';
import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { format } from 'date-fns';
import { t } from 'i18next';
import { json2csv } from 'json-2-csv';
import { useEffect, useState } from 'react';
import { DownloadIcon } from '~components/ui/icons';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { PageLoader } from '~components/ui/PageLoader';
import { PageSizeSelector } from '~components/ui/PageSizeSelector';
import { PaginationServerSide } from '~components/ui/PaginationServerSide';
import { ActivityLogAction, ActivityLogObjectType } from '~graphql/__generated__/types';
import { HandleApiError } from '~graphql/HandleApiError';
import { fromError } from '~utils/errors';
import {
  ActivityLogsDocument,
  ActivityLogsQuery,
  ActivityLogsQueryVariables,
  useActivityLogsQuery,
} from './__generated__/ActivityLogsModal.graphql';
import { TruncatedCell } from './AccessRequestsSection';

const PAGE_SIZES = [8, 25, 50, 100];
const DOWNLOAD_PAGE_SIZE = 200;

type ActivityLog = NonNullable<ActivityLogsQuery['activityLogs']>['activityLogs'][number];

function humanize(words: string[]) {
  const text = words.join(' ').toLowerCase();

  return text.charAt(0).toUpperCase() + text.slice(1);
}

function formatObjectType(objectType: ActivityLogObjectType) {
  return humanize(objectType.split('_'));
}

function formatAction(action: ActivityLogAction, objectType: ActivityLogObjectType) {
  const actionWords = action.split('_');
  const typeWords = objectType.split('_');
  let start = 0;

  while (start < typeWords.length && actionWords[start] === typeWords[start]) {
    start++;
  }

  return humanize(actionWords.slice(start));
}

interface ActivityLogRow {
  startTime: string;
  actionTakenBy: string;
  action: string;
  endpoint: string;
  nameAlias: string;
  objectIds: string;
  customerName: string;
}

interface ActivityLogColumn {
  id: keyof ActivityLogRow;
  header: string;
  maxWidth?: string;
}

function getColumns(): ActivityLogColumn[] {
  return [
    { id: 'startTime', header: t('startTime') },
    { id: 'actionTakenBy', header: t('actionTakeBy'), maxWidth: '40' },
    { id: 'action', header: t('activityLogAction'), maxWidth: '40' },
    { id: 'endpoint', header: t('activityLogEndpoint') },
    { id: 'nameAlias', header: t('activityLogNameAlias'), maxWidth: '40' },
    { id: 'objectIds', header: t('activityLogObjectIds'), maxWidth: '40' },
    { id: 'customerName', header: t('activityLogCustomerName'), maxWidth: '40' },
  ];
}

function joinOrDash(values: Array<string | null>) {
  return values.length > 0 ? values.map((value) => value ?? '-').join(', ') : '-';
}

function toActivityLogRow(log: ActivityLog): ActivityLogRow {
  return {
    startTime: format(new Date(log.startTime), 'MMM d, y, HH:mm'),
    actionTakenBy: log.actorName ?? '-',
    action: formatAction(log.action, log.objectType),
    endpoint: formatObjectType(log.objectType),
    nameAlias: joinOrDash(log.objectNames),
    objectIds: joinOrDash(log.objectIds),
    customerName: log.targetCustomerName ?? '-',
  };
}

interface Props {
  accessRequestId: string;
  organizationName: string;
  isOpen: boolean;
  onClose: () => void;
}

export function ActivityLogsModal({ accessRequestId, organizationName, isOpen, onClose }: Props) {
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [pageIndex, setPageIndex] = useState(0);

  useEffect(() => {
    setPageIndex(0);
  }, [pageSize]);

  const { data, previousData, loading, error } = useActivityLogsQuery({
    variables: { accessRequestId, pageSize, pageNumber: pageIndex + 1 },
    fetchPolicy: 'cache-and-network',
    skip: !isOpen,
  });

  const page = (data ?? previousData)?.activityLogs;
  const logs = page?.activityLogs ?? [];
  const pageCount = Math.ceil((page?.totalCount ?? 0) / pageSize);
  const columns = getColumns();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" autoFocus={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader
          marginX="6"
          paddingX="0"
          borderBottom="1px solid"
          borderColor="gray.100"
          fontSize="xl"
        >
          {t('activityLogsTitle')}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody paddingTop="4">
          {error ? (
            <HandleApiError error={error} />
          ) : (
            <>
              <Box
                boxShadow="elevated"
                border="1px solid"
                borderRadius="md"
                borderColor="gray.100"
                overflowX="auto"
                overflowY="auto"
                maxHeight="60vh"
                bgColor="white"
              >
                {loading && !page ? (
                  <Box padding="8">
                    <PageLoader />
                  </Box>
                ) : (
                  <Table variant="simple" width="100%">
                    <Thead bgColor="gray.50" position="sticky" top="0" zIndex="1">
                      <Tr>
                        {columns.map((column) => (
                          <Th
                            key={column.id}
                            color="gray.700"
                            textTransform="none"
                            whiteSpace="nowrap"
                            letterSpacing="normal"
                          >
                            {column.header}
                          </Th>
                        ))}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {logs.length === 0 ? (
                        <Tr>
                          <Td colSpan={columns.length} textAlign="center" color="gray.500">
                            {t('noActivityLogs')}
                          </Td>
                        </Tr>
                      ) : (
                        logs.map((log) => {
                          const row = toActivityLogRow(log);

                          return (
                            <Tr key={log.id}>
                              {columns.map((column) => (
                                <Td key={column.id} whiteSpace="nowrap">
                                  {column.maxWidth ? (
                                    <TruncatedCell
                                      text={row[column.id]}
                                      maxWidth={column.maxWidth}
                                    />
                                  ) : (
                                    row[column.id]
                                  )}
                                </Td>
                              ))}
                            </Tr>
                          );
                        })
                      )}
                    </Tbody>
                  </Table>
                )}
              </Box>

              <Box display="flex" marginTop="4">
                <Box flex="1">
                  <PageSizeSelector
                    label={t('showRowsPerPage')}
                    options={PAGE_SIZES}
                    value={pageSize}
                    onChange={setPageSize}
                  />
                </Box>
                <PaginationServerSide
                  pageCount={pageCount}
                  currentPageIndex={pageIndex}
                  hasPreviousPage={pageIndex > 0}
                  hasNextPage={pageIndex < pageCount - 1}
                  goToPage={setPageIndex}
                  goToPreviousPage={() => setPageIndex(pageIndex - 1)}
                  goToNextPage={() => setPageIndex(pageIndex + 1)}
                />
              </Box>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <DownloadLogsButton
            accessRequestId={accessRequestId}
            organizationName={organizationName}
            isDisabled={!page || page.totalCount === 0}
          />
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function DownloadLogsButton({
  accessRequestId,
  organizationName,
  isDisabled,
}: {
  accessRequestId: string;
  organizationName: string;
  isDisabled: boolean;
}) {
  const client = useApolloClient();
  const toast = useToast();
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);

    try {
      const logs: ActivityLog[] = [];
      let totalCount = Infinity;

      for (let pageNumber = 1; logs.length < totalCount; pageNumber++) {
        const { data } = await client.query<ActivityLogsQuery, ActivityLogsQueryVariables>({
          query: ActivityLogsDocument,
          variables: { accessRequestId, pageSize: DOWNLOAD_PAGE_SIZE, pageNumber },
          fetchPolicy: 'network-only',
        });
        const page = data.activityLogs?.activityLogs ?? [];

        totalCount = data.activityLogs?.totalCount ?? 0;
        logs.push(...page);

        if (page.length === 0) {
          break;
        }
      }

      const columns = getColumns();
      const csvData = await json2csv(
        logs
          .map(toActivityLogRow)
          .map((row) => Object.fromEntries(columns.map(({ id, header }) => [header, row[id]]))),
        { emptyFieldValue: '' },
      );
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.setAttribute('download', `${organizationName}-activity-logs.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({
        status: 'error',
        title: t('activityLogsDownloadFailed'),
        description: fromError(err, 'ActivityLogs'),
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Button
      variant="solid"
      colorScheme="blue"
      rightIcon={<DownloadIcon />}
      isLoading={isDownloading}
      isDisabled={isDisabled}
      onClick={handleDownload}
    >
      {t('downloadAllLogs')}
    </Button>
  );
}

ActivityLogsModal.graphql = {
  queries: {
    ActivityLogs: gql`
      query ActivityLogs($accessRequestId: ID!, $pageSize: Int, $pageNumber: Int) {
        activityLogs(
          accessRequestId: $accessRequestId
          pageSize: $pageSize
          pageNumber: $pageNumber
        ) {
          totalCount
          activityLogs {
            id
            startTime
            actorName
            action
            objectType
            objectIds
            objectNames
            targetCustomerName
          }
        }
      }
    `,
  },
};
